import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProgressBar } from '../../context/ProgressBarContext';
import { updateUserBasicDetails } from '../../utils/api';
import styles from './BasicDetailsPage.module.css';
import RegistrationSteps from '../../components/RegistrationSteps';
import MessageBox from '../../components/MessageBox';

function BasicDetailsPage() {
  const [formData, setFormData] = useState({
    name: '',
    mobileNumber: '',
    email: '',
    password: '',
    reenterPassword: '',
  });

  const [errorMessage, setErrorMessage] = useState(null);
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const { showProgressBar, updateProgressBar, hideProgressBar } = useProgressBar();

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        mobileNumber: user.mobileNumber || '',
        email: user.email || '',
        password: '',
        reenterPassword: '',
      });
    }
  }, [user]);

  const { name, mobileNumber, email, password, reenterPassword } = formData;

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const validate = () => {
    let tempErrors = {};
    if (!name) tempErrors.name = 'Full name is required.';
    if (!mobileNumber.match(/^[0-9]{10}$/)) tempErrors.mobileNumber = 'Mobile number must be 10 digits.';
    if (!email.match(/^\S+@\S+\.\S+$/)) tempErrors.email = 'Email is not valid.';
    if (password && password.length < 6) tempErrors.password = 'Password must be at least 6 characters.';
    if (password && password !== reenterPassword) tempErrors.reenterPassword = 'Passwords do not match.';
    
    if (Object.keys(tempErrors).length > 0) {
      setErrorMessage(Object.values(tempErrors)[0]);
      return false;
    }
    return true;
  };

  const navigateToNextStep = (updatedUser) => {
    if (updatedUser.kycStatus === 'not submitted') {
      navigate('/kyc');
    } else if (updatedUser.additionalDetailsStatus === 'not submitted') {
      navigate('/additional-details');
    } else if (updatedUser.bankStatus === 'not submitted') {
      navigate('/bank-details');
    } else {
      navigate('/account-status');
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    if (validate()) {
      showProgressBar();
      updateProgressBar(50);
      try {
        const updatedUserData = { name, mobileNumber, email };
        if (password) {
          updatedUserData.password = password;
        }
        const { data: updatedUser } = await updateUserBasicDetails(updatedUserData);
        updateUser(updatedUser);
        navigateToNextStep(updatedUser);
      } catch (err) {
        setErrorMessage(err.response?.data?.msg || 'An unexpected error occurred. Please try again.');
      } finally {
        hideProgressBar();
      }
    }
  };

  return (
    <div className={styles.basicDetailsPageWrapper}>
      {errorMessage && <MessageBox message={errorMessage} type="error" onClose={() => setErrorMessage(null)} />}
      
      <RegistrationSteps currentStep={1} />

      <div className={styles.basicDetailsContainer}>
        <div className={styles.basicDetailsFormSection}>
          <div className={styles.formHeader}>
            <h2>Basic Details</h2>
            <p>Step 1: Your personal information</p>
          </div>

          <form onSubmit={onSubmit}>
            <div className={styles.formGroup}>
              <label htmlFor="name">Full Name</label>
              <input type="text" id="name" name="name" value={name} onChange={onChange} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="mobileNumber">Mobile Number</label>
              <input type="text" id="mobileNumber" name="mobileNumber" value={mobileNumber} onChange={onChange} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email</label>
              <input type="email" id="email" name="email" value={email} onChange={onChange} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="password">New Password (optional)</label>
              <input type="password" id="password" name="password" value={password} onChange={onChange} placeholder="Leave blank to keep current" />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="reenterPassword">Confirm Password</label>
              <input type="password" id="reenterPassword" name="reenterPassword" value={reenterPassword} onChange={onChange} placeholder="Confirm new password" />
            </div>
            <button type="submit" className={styles.btnSubmit}>Continue to KYC</button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BasicDetailsPage;
